













































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { required, url, email } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { helpers } from 'vuelidate/lib/validators';
import { PublishedApp } from '@/features/apps/shared/models';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import GoogleBmLocations from '@/features/apps/googlebm/components/GoogleBmLocations.vue';
import TriageIcon from '@/shared/components/TriageIcon.vue';
import TagsInput from '@/shared/components/TagsInput.vue';
import { GoogleBmAgent } from '@/features/apps/googlebm/models/googleBmAgent';
import { AgentConfigRequest } from '@/features/apps/googlebm/models/agentConfigRequest';
import {BusinessHoursViewModel} from '@/features/bots/models';
import { VerificationStatus } from '@/features/apps/googlebm/models/verificationStatus';
import Toast from '@/shared/services/toast';
import { ConfigLocation } from '../models/configLocation';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;
const internationalPhoneNumber = helpers.regex('phone', E164_REGEX);
const https = helpers.regex('url', /^https.*/);

@Component({
	mixins: [validationMixin],
	validations: {
		agentConfig: {
			brandDisplayName: { required },
			brandLogoUrl: { required, url, https },
			brandWebsiteUrl: { required, url, https },
			brandContactName: { required },
			brandContactEmail: { required, email },
			welcomeMessage: { required },
			privacyPolicyUrl: { required, url, https },
			nonLocalPhoneNumber: { required, internationalPhoneNumber },
			brand: [
				'agentConfig.brandDisplayName',
				'agentConfig.nonLocalPhoneNumber',
				'agentConfig.brandWebsiteUrl',
				'agentConfig.privacyPolicyUrl',
				'agentConfig.brandContactName',
				'agentConfig.brandContactEmail'
			],
			agent: [
				'agentConfig.brandLogoUrl',
				'agentConfig.welcomeMessage',
			],
		},
	},
	components: {
		TriageIcon,
		TagsInput,
		GoogleBmLocations
	}
})
export default class GoogleBmAgentConfig extends Vue {
	@Getter public currentApp!: PublishedApp;
	@Getter public currentWorkspace!: WorkspaceViewModel;

	@Prop() public agent: GoogleBmAgent | null;
	@Prop() public humanAvailability: BusinessHoursViewModel[] | null;
	@Prop({default: false}) public saving: boolean;

	public agentConfig: AgentConfigRequest = {};
	public actionInProgress: boolean = false;
	public disabledFields: string[] = [];

	@Watch('humanAvailability', { immediate: false })
	public onHumanAvailabilityChange(loadedHours: BusinessHoursViewModel[]) {
		this.agentConfig.humanAvailability = loadedHours;
	}

	@Watch('saving', { immediate: false })
	public onSavingChange(saving: boolean) {
		this.actionInProgress = saving;
	}

	public mounted() {
		if (this.agent) {
			this.agentConfig = {
				brandDisplayName: this.agent.brandDisplayName,
				brandLogoUrl: this.agent.brandLogoUrl,
				brandWebsiteUrl: this.agent.brandWebsiteUrl,
				brandContactName: this.agent.brandContactName,
				brandContactEmail: this.agent.brandContactEmail,
				agentDisplayName: this.agent.displayName,
				welcomeMessage: this.agent.welcomeMessage,
				conversationStarters: this.agent.conversationStarters,
				privacyPolicyUrl: this.agent.privacyPolicyUrl,
				nonLocalContactWebsiteUrl: this.agent.nonLocalContactWebsiteUrl,
				nonLocalPhoneNumber: this.agent.nonLocalPhoneNumber,
				nonLocalCallDeflectionPhoneNumbers: this.agent.nonLocalCallDeflectionPhoneNumbers,
				locations: this.agent.locations
			};

			if (this.hasAgentRequestedVerification(this.agent)) {
				this.disabledFields = [
					'agentDisplayName',
					'brandDisplayName',
					'brandLogoUrl',
					'brandWebsiteUrl',
					'brandContactName',
					'brandContactEmail',
					'nonLocalContactWebsiteUrl',
					'nonLocalPhoneNumber',
					'nonLocalCallDeflectionPhoneNumbers'
				];
			}
		}
	}

	public async save() {
		if (this.$v.agentConfig.$invalid) {
			this.showAllFormErrors();
			return;
		}
		if (this.actionInProgress) {
			return;
		}

		this.actionInProgress = true;

		const request: AgentConfigRequest = {};
		Object.entries(this.agentConfig).forEach(([key, value]) => {
			if (!this.isDisabledField(key)) {
				(request as any)[key] = value;
			}
		});
		if  (!this.isDisabledField('nonLocalContactWebsiteUrl')) {
			request.nonLocalContactWebsiteUrl = this.agentConfig.brandWebsiteUrl;
		}
		if  (!this.isDisabledField('agentDisplayName')) {
			request.agentDisplayName = this.agentConfig.brandDisplayName;
		}
		if (!this.isDisabledField('nonLocalCallDeflectionPhoneNumbers')) {
			request.nonLocalCallDeflectionPhoneNumbers = [this.agentConfig.nonLocalPhoneNumber as string];
		}

		this.$emit('save', request);
	}

	public cancel() {
		this.$emit('cancel');
	}

	public isDisabledField(key: string): boolean {
		return this.disabledFields.includes(key);
	}

	public markAsTouched(fieldName: string) {
		const field = this.$v.agentConfig[fieldName];
		if (field) {
			field.$touch();
		}
	}

	public getErrorMessage(fieldName: string, label: string): string | null {
		const field = this.$v.agentConfig[fieldName];
		if (field && field.$dirty && field.$invalid) {
			if (field.$params.required && !field.required) {
				return `${label} is required`;
			}
			if (field.$params.url && !field.url) {
				return `${label} must be a valid URL`;
			}
			if (field.$params.https && !field.https) {
				return `${label} must be https`;
			}
			if (field.$params.email && !field.email) {
				return `${label} must be a valid email address`;
			}
			if (field.$params.numeric && !field.numeric) {
				return `${label} must be a number`;
			}
			if (field.$params.internationalPhoneNumber && !field.internationalPhoneNumber) {
				return `${label} must be a valid phone number, e.g. (+44123456789)`;
			}
		}
		return null;
	}

	public handleLocationChange(locations: ConfigLocation[]) {
		this.agentConfig.locations = locations;
	}

	private showAllFormErrors() {
		for (const validationField in this.$v.agentConfig) {
			if (!validationField.startsWith('$')) {
				this.markAsTouched(validationField);
			}
		}
		Toast.showError(`Please fix validation errors before continuing`);
	}

	private hasAgentRequestedVerification(agent: GoogleBmAgent): boolean {
		return (
			agent.verificationStatus === VerificationStatus.VERIFICATION_STATE_PENDING ||
			agent.verificationStatus === VerificationStatus.VERIFICATION_STATE_VERIFIED ||
			agent.verificationStatus === VerificationStatus.VERIFICATION_STATE_SUSPENDED_IN_GMB
		);
	}
}
